/**
 * Barre d'actions partagée entre la page Interview et la page Analyse.
 * - Gauche  : titre (processus ou page)
 * - Droite  : ThemeSwitcher, langue, aide, CTA
 *
 * Pour l'interview : passer `processTitle` + `processType`  → CTA = Link vers création d'entretien
 * Pour l'analyse   : passer `processTitle` + `ctaLabel` + `ctaOnClick` → CTA = bouton
 */
import React from "react"
import { Link, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"

import Dropdown from "../../components/Dropdown"
import Routes from '../../routes/Routes'
import { getProcessCreatePath } from "../../utils/processCreateRoute"
import { openPDFInViewer } from "../../utils"
import ThemeSwitcher from "../../components/ThemeSwitcher"
import type { typeProcess } from "../../components/type"
import { i18n } from "../../services"
import { trackUiButtonEventAsync, UI_BUTTON_EVENTS } from "../../services/api/uiButtonStatsService"

import "../../layouts/header-redesign.css"
import "../../styles/interview-redesign.css"

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function planLabelFromStatus(t: TFunction, status?: string): string {
    if (!status) return t("layout.header.planUnknown")
    const s = status.toUpperCase()
    const keys: Record<string, string> = {
        FREE: "layout.header.planFREE",
        ESSENTIAL: "layout.header.planESSENTIAL",
        PLUS: "layout.header.planPLUS",
        ENTERPRISE: "layout.header.planENTERPRISE",
    }
    const key = keys[s]
    if (key) return t(key)
    return s.charAt(0) + s.slice(1).toLowerCase()
}

export interface InterviewContextTopBarProps {
    /** Titre affiché à gauche (nom du processus ou de la page) */
    processTitle: string
    /**
     * Interview : type de processus pour générer le lien du CTA.
     * Non requis pour la page Analyse.
     */
    processType?: typeProcess
    /**
     * Libellé personnalisé du bouton CTA.
     * Si fourni avec `ctaOnClick`, le CTA devient un <button> au lieu d'un <Link>.
     */
    ctaLabel?: string
    /**
     * Handler pour le CTA en mode bouton (page Analyse).
     * Si absent, le CTA est un <Link> vers la création d'entretien (page Interview).
     */
    ctaOnClick?: () => void
    /** Actions supplémentaires à afficher à côté des boutons standards */
    extraActions?: React.ReactNode
    /** Contenu affiché à droite du titre (ex. badge de statut data collect) */
    titleAddon?: React.ReactNode
    /** Masquer le lien / bouton « nouvel entretien » (ex. page contributeur data collect) */
    hideCreateCta?: boolean
}

const InterviewContextTopBar: React.FC<InterviewContextTopBarProps> = ({
    processTitle,
    processType,
    ctaLabel,
    ctaOnClick,
    extraActions,
    titleAddon,
    hideCreateCta = false,
}) => {
    const { t } = useTranslation()
    const navigate = useNavigate()

    const [currentLanguage, setCurrentLanguage] = React.useState(() => {
        try {
            return localStorage.getItem("currentLanguage") || navigator.language
        } catch {
            return navigator.language
        }
    })

    const updateLang = async (lang: string) => {
        i18n.changeLanguage(lang === "fr" ? "fr" : "en")
        setCurrentLanguage(lang === "fr" ? "fr" : "en")
        try {
            localStorage.setItem("currentLanguage", lang === "fr" ? "fr" : "en")
        } catch {
            /* ignore */
        }
    }

    const changeLanguage = (language: string) => {
        const updateLanguage = language === "fr-FR" || language === "fr" ? "fr" : "en"
        void updateLang(updateLanguage)
    }

    const title = (processTitle || "").trim() || t("words.loading")

    const ctaButtonLabel = ctaLabel ?? t("views.processInterview.newInterview")

    return (
        <div className="interview-ctx-topbar interview-ctx-topbar--hdr-tokens">
            {/* GAUCHE : titre identique pour les deux pages */}
            <div className="interview-ctx-topbar__left">
                {titleAddon ? (
                    <div className="interview-ctx-topbar__title-row">
                        <span className="interview-ctx-topbar__title" title={title}>
                            {title}
                        </span>
                        <span className="interview-ctx-topbar__title-addon">{titleAddon}</span>
                    </div>
                ) : (
                    <span className="interview-ctx-topbar__title" title={title}>
                        {title}
                    </span>
                )}
            </div>

            {/* DROITE : actions identiques pour les deux pages */}
            <div className="interview-ctx-topbar__actions">
                <ThemeSwitcher />

                {/* Sélecteur de langue */}
                <Dropdown
                    onMenuButtonClick={() => {
                        void trackUiButtonEventAsync(
                            UI_BUTTON_EVENTS.interview.topbarLangOpen
                        );
                    }}
                    dropName={
                        <>
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden>
                                <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.2" />
                                <path d="M8 2.5C8 2.5 6 5 6 8C6 11 8 13.5 8 13.5" stroke="currentColor" strokeWidth="1.2" />
                                <path d="M8 2.5C8 2.5 10 5 10 8C10 11 8 13.5 8 13.5" stroke="currentColor" strokeWidth="1.2" />
                                <path d="M2.5 8H13.5" stroke="currentColor" strokeWidth="1.2" />
                            </svg>
                            {currentLanguage === "fr" ? t("layout.header.langShortFR") : t("layout.header.langShortEN")}
                        </>
                    }
                    dropdownType="dropdown"
                    buttonDropClassName="dropdown-toggle hdr-dd-btn interview-ctx-dd"
                    styleButton={{}}
                    theRouteOfAction4=""
                    theRouteOfAction5=""
                    lang
                    changeLanguage={changeLanguage}
                />

                {/* Aide */}
                <Dropdown
                    onMenuButtonClick={() => {
                        void trackUiButtonEventAsync(
                            UI_BUTTON_EVENTS.interview.topbarHelpOpen
                        );
                    }}
                    dropName={
                        <>
                            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden>
                                <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.2" />
                                <path d="M6.5 6.5C6.5 5.7 7.2 5 8 5C8.8 5 9.5 5.7 9.5 6.5C9.5 7.3 8 8 8 9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                                <circle cx="8" cy="11" r=".8" fill="currentColor" />
                            </svg>
                            {t("words.help")}
                        </>
                    }
                    dropdownType="dropstart"
                    buttonDropClassName="dropdown-toggle hdr-dd-btn hdr-dd-help interview-ctx-dd"
                    styleButton={{}}
                    deleteProcess={() => {}}
                    action4={t("navigation.contactUs")}
                    action5={t("navigation.privacyHub")}
                    theRouteOfAction4={Routes.CONTACTUS}
                    theRouteOfAction5={Routes.PRIVACY}
                    faq={t("words.faq")}
                    routeFaq={Routes.FAQ}
                    editdiadram={t("navigation.userGuide")}
                    runEditor={openPDFInViewer}
                />

                {/* CTA : bouton (analyse) ou lien (interview) — masquable (contributeur collect) */}
                {!hideCreateCta &&
                    (ctaOnClick ? (
                        <button
                            type="button"
                            className="action-btn primary interview-ctx-new-interview"
                            onClick={async () => {
                                await trackUiButtonEventAsync(
                                    UI_BUTTON_EVENTS.interview.topbarNewInterview
                                );
                                ctaOnClick();
                            }}
                        >
                            <svg width="11" height="11" viewBox="0 0 16 16" fill="none" aria-hidden>
                                <path d="M8 2V14M2 8H14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                            </svg>
                            {ctaButtonLabel}
                        </button>
                    ) : (
                        <Link
                            to={getProcessCreatePath(processType ?? 'DEFAULT')}
                            className="action-btn primary interview-ctx-new-interview"
                            onClick={async (e) => {
                                e.preventDefault();
                                await trackUiButtonEventAsync(
                                    UI_BUTTON_EVENTS.interview.topbarNewInterview
                                );
                                navigate(
                                    getProcessCreatePath(processType ?? "DEFAULT")
                                );
                            }}
                        >
                            <svg width="11" height="11" viewBox="0 0 16 16" fill="none" aria-hidden>
                                <path d="M8 2V14M2 8H14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                            </svg>
                            {ctaButtonLabel}
                        </Link>
                    ))}

                {extraActions}
            </div>
        </div>
    )
}

export default InterviewContextTopBar
